import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Text, Button, Icon } from '@deseretbook/react-ui'

import './MarkupsSidebar.css'
import Markup from './Markup'
import { deleteAnnotation } from '../../../api/media'

const TABS = {
  highlights: {
    key: 'highlights',
    label: 'Highlights',
    activeIcon: 'highlightSolid',
    icon: 'highlight',
  },
  notes: { key: 'notes', label: 'Notes', activeIcon: 'noteSolid', icon: 'note' },
  // tags: { key: 'tags', label: 'Tags', activeIcon: 'tagSolid', icon: 'tag' },
}

/**
 * MarkupsSidebar component to display and manage book annotations like highlights, notes, and tags.
 */
const MarkupsSidebar = ({
  onClose,
  isOpen,
  annotations,
  tableOfContents,
  onMarkupClick,
  onMarkupEdit,
  ...rest
}) => {
  const [tab, setTab] = useState('highlights')
  const [markups, setMarkups] = useState({ highlights: [], notes: [], tags: [], other: [] })

  /**
   * Builds categorized lists of annotations based on their type (highlights, notes, tags).
   * @param {Object} annotationsList - The list of annotations to categorize.
   */
  const buildCategoryLists = (annotationsList) => {
    const buckets = { highlights: [], notes: [], tags: [], other: [] }

    Object.values(annotationsList).forEach((annArr) => {
      annArr.forEach((ann) => {
        const { note, highlight, tag } = ann
        if (note) buckets.notes.push(ann)
        else if (tag) buckets.tags.push(ann)
        else if (highlight) buckets.highlights.push(ann)
        else buckets.other.push(ann)
      })
    })

    setMarkups(buckets)
  }

  /**
   * Handles the removal of a markup (highlight, note, or tag).
   * @param {Object} markup - The markup object to remove.
   */
  const onRemoveMarkup = async (markup) => {
    try {
      await deleteAnnotation(markup)
      // Refresh the page
      window.location.reload()
    } catch (e) {
      console.error(e)
    }
  }

  // Build the initial category lists when annotations are provided
  useEffect(() => {
    if (annotations) buildCategoryLists(annotations)
  }, [annotations])

  const currentTabConfig = TABS[tab]
  const currentList = markups[currentTabConfig.key] || []

  return (
    <div id="MarkupsSidebarContainer" {...rest}>
      {/* header */}
      <Box alignItems="center" justifyContent="space-between" padding={1}>
        <Button onClick={onClose} className="sidebar-button-close">
          <Icon name="arrowRight" className="sidebar-button-icon" />
        </Button>
        <Text style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 700, fontSize: 20 }}>
          Markups
        </Text>
        <Box height="40px" width="40px" />
      </Box>

      {/* tab bar */}
      <Box className="markups-tab-container" alignItems="center" justifyContent="space-between">
        {Object.entries(TABS).map(([tabId, { activeIcon, icon }]) => {
          const isActive = tab === tabId
          return (
            <Button
              key={tabId}
              className="markups-tab-button"
              style={{
                backgroundColor: isActive ? 'rgba(255,255,255,0.95)' : 'transparent',
                border: isActive ? '1px solid rgba(0,0,0,0.10)' : '1px solid transparent',
                opacity: isActive ? 1 : 0.7,
              }}
              onClick={() => setTab(tabId)}
            >
              <Icon
                className="markups-tab-icon"
                name={isActive ? activeIcon : icon}
                svgProps={{ height: isActive ? '20' : '24', width: isActive ? '20' : '24' }}
                style={{
                  height: isActive ? '20px' : '24px',
                  width: isActive ? '20px' : '24px',
                }}
              />
            </Button>
          )
        })}
      </Box>

      {/* list */}
      <Box className="markups-content">
        <Text style={{ fontFamily: 'Roboto, sans-serif', fontSize: 16, fontWeight: 700 }}>
          {currentTabConfig.label}{' '}
          <Text as="span" style={{ color: 'rgba(0,0,0,0.65)', fontWeight: 400 }}>
            ({currentList.length})
          </Text>
        </Text>

        {currentList.map((markup, index) => (
          <Markup
            key={index}
            markup={markup}
            tableOfContents={tableOfContents}
            onMarkupClick={onMarkupClick}
            onRemoveMarkup={onRemoveMarkup}
            markupType={tab}
            onMarkupEdit={onMarkupEdit}
            handleClose={onClose}
          />
        ))}
      </Box>
    </div>
  )
}

MarkupsSidebar.defaultProps = {
  tableOfContents: [],
  onSelectChapter: () => {},
  isVisible: true,
  className: '',
}

MarkupsSidebar.propTypes = {
  tableOfContents: PropTypes.arrayOf(PropTypes.object),
  onSelectChapter: PropTypes.func,
  isVisible: PropTypes.bool,
  className: PropTypes.string,
}

export default MarkupsSidebar
